import { useState, useEffect, useRef } from 'react'
import { getIsLive } from '../../helpers'
import { getNowTimestamp } from '../../helpers/dateTimeHelpers'

/**
 * Reactive hook that tracks whether a video is currently live.
 * Updates automatically when the video goes live or ends.
 *
 * @param {number} videoStartTime - Video start timestamp in milliseconds
 * @param {number|string} videoDuration - Video duration in milliseconds or duration string
 * @returns {boolean} Whether the video is currently live
 */
export default function useIsLive(videoStartTime, videoDuration) {
  const [isLive, setIsLive] = useState(() => {
    if (!videoStartTime || !videoDuration) return false
    return getIsLive({ date: videoStartTime, videoDuration })
  })

  const timeoutRef = useRef()

  useEffect(() => {
    // If no start time or duration, can't calculate live status
    if (!videoStartTime || !videoDuration) {
      setIsLive(false)
      return undefined
    }

    const now = getNowTimestamp()
    const currentIsLive = getIsLive({ date: videoStartTime, videoDuration, now })

    // Update state if it changed
    if (currentIsLive !== isLive) {
      setIsLive(currentIsLive)
    }

    // Calculate when the next state change will occur
    let nextChangeTime

    if (!currentIsLive) {
      // Video hasn't started yet - check if it will start in the future
      if (now < videoStartTime) {
        // Schedule update for when video goes live
        nextChangeTime = videoStartTime - now
      }
      // If video has already ended (now > videoStartTime + videoDuration),
      // no need to schedule anything
    } else {
      // Video is currently live - schedule update for when it ends
      const videoEndTime = videoStartTime + (typeof videoDuration === 'string' ? 0 : videoDuration)
      nextChangeTime = videoEndTime - now
    }

    // Schedule the next state update if there is one
    if (nextChangeTime && nextChangeTime > 0) {
      timeoutRef.current = setTimeout(() => {
        const newIsLive = getIsLive({ date: videoStartTime, videoDuration })
        setIsLive(newIsLive)
      }, nextChangeTime)
    }

    return () => {
      clearTimeout(timeoutRef.current)
    }
  }, [videoStartTime, videoDuration, isLive])

  return isLive
}
